import React, { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Row, Col, Card, Statistic, Spin, Alert } from 'antd';
import ReactECharts from 'echarts-for-react';
import { fetchDashboardData, clearError } from '../features/dashboard/dashboardSlice';
import dayjs from 'dayjs';
import { getDashboardTimeRange } from '../utils';

const Dashboard = () => {
  const dispatch = useDispatch();
  const { thirdPartyStats, charts, isLoading, error } = useSelector((state) => state.dashboard);

  useEffect(() => {
    const load = () => {
      const { today } = getDashboardTimeRange();
      dispatch(fetchDashboardData({ startTs: today.startTs, endTs: today.endTs }));
    };
    load();
    const interval = setInterval(load, 30000);
    return () => clearInterval(interval);
  }, [dispatch]);

  useEffect(() => {
    if (error) dispatch(clearError());
  }, [error, dispatch]);

  if (error) return <Alert message="加载失败" description={error} type="error" showIcon />;

  const formatAmount = (val) => val ? Number(val).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) : '0.00';

  const payChartData = charts?.thirdPartyChart?.payChart || [];
  const withdrawChartData = charts?.thirdPartyChart?.withdrawChart || [];

  const chartOption = {
    tooltip: { trigger: 'axis' },
    legend: { data: ['收款', '代付'], top: 10 },
    grid: { left: '3%', right: '4%', bottom: '3%', containLabel: true },
    xAxis: { 
      type: 'category', 
      data: payChartData.map(item => dayjs(item.date).format('MM-DD')) 
    },
    yAxis: { type: 'value' },
    series: [
      { 
        name: '收款', 
        type: 'bar', 
        data: payChartData.map(item => item.amount || 0),
        label: { show: true, position: 'top', formatter: (p) => p.value > 0 ? Number(p.value).toLocaleString() : '' },
        itemStyle: { color: '#1890ff' }
      },
      { 
        name: '代付', 
        type: 'bar', 
        data: withdrawChartData.map(item => item.amount || 0),
        label: { show: true, position: 'top', formatter: (p) => p.value > 0 ? Number(p.value).toLocaleString() : '' },
        itemStyle: { color: '#52c41a' }
      }
    ]
  };

  return (
    <div>
      <div className="page-header">
        <h1 className="page-title">综合看板</h1>
        <p className="page-description">第三方支付运营数据</p>
      </div>

      {isLoading ? (
        <div style={{ textAlign: 'center', padding: 50 }}>
          <Spin size="large" />
        </div>
      ) : (
        <>
          <Row gutter={[16, 16]}>
            <Col span={24}>
              <Card size="small" title="商户统计">
                <Row gutter={8}>
                  <Col span={4}>
                    <Statistic title="剩余余额" value={formatAmount(thirdPartyStats?.merchantTotalBalance)} valueStyle={{ fontSize: 20, color: '#1890ff' }} />
                  </Col>
                  <Col span={4}>
                    <Statistic title="待结算" value={formatAmount(thirdPartyStats?.totalWaitSettleAmount)} valueStyle={{ fontSize: 20, color: '#faad14' }} />
                  </Col>
                  <Col span={4}>
                    <Statistic title="待代付" value={formatAmount(thirdPartyStats?.todayWaitWithdrawAmount)} valueStyle={{ fontSize: 20, color: '#ff4d4f' }} />
                  </Col>
                  <Col span={4}>
                    <Statistic title="充值总额" value={formatAmount(thirdPartyStats?.totalMerchantRecharge)} valueStyle={{ fontSize: 20, color: '#52c41a' }} />
                  </Col>
                  <Col span={4}>
                    <Statistic title="提款总额" value={formatAmount(thirdPartyStats?.totalMerchantWithdraw)} valueStyle={{ fontSize: 20, color: '#eb2f96' }} />
                  </Col>
                </Row>
              </Card>
            </Col>
          </Row>

          <Row gutter={[16, 16]} style={{ marginTop: 16 }}>
            <Col span={24}>
              <Card size="small" title="收款订单">
                <Row gutter={8}>
                  <Col span={4}>
                    <Statistic title="待审核" value={thirdPartyStats?.pendingPayCount || 0} valueStyle={{ color: '#faad14', fontSize: 22 }} />
                  </Col>
                  <Col span={5}>
                    <Statistic title="今日金额" value={formatAmount(thirdPartyStats?.todayPayAmount)} valueStyle={{ fontSize: 22 }} />
                  </Col>
                  <Col span={4}>
                    <Statistic title="今日笔数" value={thirdPartyStats?.todayPayCount || 0} valueStyle={{ fontSize: 22 }} />
                  </Col>
                  <Col span={5}>
                    <Statistic title="总金额" value={formatAmount(thirdPartyStats?.totalPayAmount)} valueStyle={{ fontSize: 22 }} />
                  </Col>
                  <Col span={4}>
                    <Statistic title="总笔数" value={thirdPartyStats?.totalPayCount || 0} valueStyle={{ fontSize: 22 }} />
                  </Col>
                </Row>
              </Card>
            </Col>

            <Col span={24}>
              <Card size="small" title="代付订单">
                <Row gutter={8}>
                  <Col span={4}>
                    <Statistic title="待审核" value={thirdPartyStats?.pendingWithdrawCount || 0} valueStyle={{ color: '#faad14', fontSize: 22 }} />
                  </Col>
                  <Col span={5}>
                    <Statistic title="今日金额" value={formatAmount(thirdPartyStats?.todayWithdrawAmount)} valueStyle={{ fontSize: 22 }} />
                  </Col>
                  <Col span={4}>
                    <Statistic title="今日笔数" value={thirdPartyStats?.todayWithdrawCount || 0} valueStyle={{ fontSize: 22 }} />
                  </Col>
                  <Col span={5}>
                    <Statistic title="总金额" value={formatAmount(thirdPartyStats?.totalWithdrawAmount)} valueStyle={{ fontSize: 22 }} />
                  </Col>
                  <Col span={4}>
                    <Statistic title="总笔数" value={thirdPartyStats?.totalWithdrawCount || 0} valueStyle={{ fontSize: 22 }} />
                  </Col>
                </Row>
              </Card>
            </Col>
          </Row>

          <Row gutter={[16, 16]} style={{ marginTop: 16 }}>
            <Col span={24}>
              <Card size="small" title="7日收款/代付趋势">
                <ReactECharts option={chartOption} style={{ height: 280 }} />
              </Card>
            </Col>
          </Row>
        </>
      )}
    </div>
  );
};

export default Dashboard;